import { useEffect, useMemo, useState } from "react";
import { ChevronRight, MapPin, StickyNote } from "lucide-react";

import { EstadoDeError } from "@/components/estados/EstadoDeError";
import { EstadoSinConexion } from "@/components/estados/EstadoSinConexion";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { useMarcarRevisado, useRegistroDeTerreno } from "@/hooks/useRegistroDeTerreno";
import { estadoDeConsulta } from "@/lib/estadoDeConsulta";
import { PESTANAS, type SeccionInforme } from "@/lib/informes";
import { obtenerIniciales } from "@/lib/iniciales";
import type {
  AudioDeTerreno,
  CampoDeTerreno,
  ItemDeTerreno,
  RegistroDeTerreno,
  SeccionDeTerreno,
} from "@/lib/terreno";
import { cn } from "@/lib/utils";

import { FiltroDeSincronizaciones } from "./FiltroDeSincronizaciones";
import { BotonDescartarDeTerreno } from "./DescarteDeTerreno";
import { ClasificadorDeCaptura, FilaClasificadaEnLaWeb } from "./ClasificadorDeCaptura";
import { TarjetaDeFoto, VisorDeFotos } from "./FotosDeTerreno";
import { PapeleraDeTerreno } from "./PapeleraDeTerreno";
import { CAMPOS_DE_CONSTRUCCION, etiquetaDelCampoDeConstruccion } from "./notasDeConstruccion";
import { TarjetaDeNotasGenerales } from "./NotasGeneralesDeTerreno";
import { ReproductorDeAudioDeTerreno } from "./ReproductorDeAudioDeTerreno";
import {
  avisoDeTranscripcion,
  campoVisible,
  conteoDeLaSeccion,
  conteosPorFiltro,
  descripcionDelConteo,
  fotosDelVisor,
  horaDeCaptura,
  itemVisible,
  momentoDeSincronizacion,
  numeroDeRegistro,
  pluralizar,
  rangoDeLaVisita,
  registroVisible,
  tieneContenido,
  tiposDelRegistro,
  totalDeMaterial,
  urlDelMapaDeTerreno,
  visibleConElFiltro,
  fotosDelRegistro,
  type FiltroDeTerreno,
} from "./registroDeTerreno";
import { cantidadDeNotas } from "./notasGenerales";
import {
  filtrarNotasGeneralesPorSincronizacion,
  filtrarPorSincronizacion,
} from "./sincronizaciones";

const FILTROS: { clave: FiltroDeTerreno; etiqueta: string }[] = [
  { clave: "todo", etiqueta: "Todo" },
  { clave: "fotos", etiqueta: "Fotos" },
  { clave: "audios", etiqueta: "Audios" },
  { clave: "textos", etiqueta: "Textos" },
];

const idDeLaSeccion = (clave: string) => `terreno-seccion-${clave}`;

interface PestanaDeTerrenoProps {
  informeId: number;
  /** Cambia a la pestaña del editor donde vive lo que esa sección describe. */
  onAbrirPestana: (pestana: SeccionInforme) => void;
}

/**
 * Lo que el encargado sincronizó desde la App de terreno. No se edita: lo único
 * que se escribe desde acá es DESCARTAR un bloque (a la papelera, reversible).
 *
 * Nada de lo que se ve acá se escribe: es el espejo de `informe_terreno_registros`
 * compuesto por el API (`VistaDelRegistroDeTerreno`), y el único acto posible
 * es mirar, escuchar o saltar a la pestaña donde ese dato se carga de verdad.
 *
 * LA TRANSCRIPCIÓN TODAVÍA NO EXISTE: cada nota de voz reserva su lugar con un
 * recordatorio en vez de esconderlo, porque el espacio es parte del diseño y el
 * día que el servidor mande el texto, solo hay que dejar de recibir `null`.
 *
 * UNA SOLA CONSULTA, compartida con el contador de la barra de pestañas.
 */
export function PestanaDeTerreno({ informeId, onAbrirPestana }: PestanaDeTerrenoProps) {
  const consulta = useRegistroDeTerreno(informeId);
  const estado = estadoDeConsulta(consulta);

  const [filtro, setFiltro] = useState<FiltroDeTerreno>("todo");
  const [fotoAbierta, setFotoAbierta] = useState<string | null>(null);

  const [sincronizacion, setSincronizacion] = useState<string | null>(null);
  const marcarRevisado = useMarcarRevisado(informeId);

  const sincronizaciones = useMemo(() => consulta.data?.sincronizaciones ?? [], [consulta.data]);
  /* Una sincronización que ya no está (la lista se rehízo) no deja la pantalla
     filtrando por algo que nadie puede ver ni quitar. */
  const sincronizacionEfectiva =
    sincronizacion !== null && sincronizaciones.some((s) => s.id === sincronizacion)
      ? sincronizacion
      : null;
  /* El filtro por sincronización va ANTES de todo lo demás: conteos, índice,
     visor y tarjetas leen la misma lista y no pueden contradecirse. */
  const secciones = useMemo(
    () => filtrarPorSincronizacion(consulta.data?.secciones ?? [], sincronizacionEfectiva),
    [consulta.data, sincronizacionEfectiva],
  );
  const notasGenerales = useMemo(
    () =>
      consulta.data
        ? filtrarNotasGeneralesPorSincronizacion(consulta.data.notas_generales, sincronizacionEfectiva)
        : undefined,
    [consulta.data, sincronizacionEfectiva],
  );
  const conteos = useMemo(() => conteosPorFiltro(secciones), [secciones]);
  const fotos = useMemo(() => fotosDelVisor(secciones, filtro), [secciones, filtro]);
  const activa = useSeccionActiva(secciones.map((s) => s.clave));

  if (estado.sinConexion) return <EstadoSinConexion que="el registro de terreno" />;

  if (estado.hayError) {
    return (
      <EstadoDeError
        titulo="No se pudo cargar el registro de terreno"
        error={consulta.error}
        onReintentar={() => void consulta.refetch()}
        reintentando={estado.recargando}
      />
    );
  }

  if (estado.cargando || !consulta.data) {
    return (
      <div
        role="status"
        aria-label="Cargando el registro de terreno"
        className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_15.125rem]"
      >
        <div className="flex flex-col gap-3.5">
          <Skeleton className="h-28 w-full rounded-xl" />
          <Skeleton className="h-48 w-full rounded-xl" />
          <Skeleton className="h-48 w-full rounded-xl" />
        </div>
        <Skeleton className="hidden h-72 w-full rounded-xl lg:block" />
      </div>
    );
  }

  const datos = consulta.data;

  function irA(clave: string) {
    document.getElementById(idDeLaSeccion(clave))?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }

  return (
    <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_15.125rem]">
      <div className="flex min-w-0 flex-col gap-3.5">
        <section className="border-border bg-card flex flex-col gap-3 rounded-xl border-[1.5px] px-[1.125rem] py-3.5">
          <div className="flex flex-wrap items-center gap-3">
            <Avatar className="size-9">
              <AvatarFallback className="bg-primary text-primary-foreground text-xs font-bold">
                {datos.encargado ? obtenerIniciales(datos.encargado.nombre) : "?"}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="text-ink text-sm font-bold">
                {datos.encargado?.nombre ?? "Sin encargado de terreno"}
              </div>
              <div className="text-ink-muted mt-px text-xs font-semibold">App de terreno</div>
            </div>
            <div className="ml-[1.125rem] flex flex-wrap gap-[1.375rem]">
              <DatoDelEncabezado rotulo="Visita" valor={rangoDeLaVisita(datos.visita)} />
              <DatoDelEncabezado
                rotulo="Última sincronización"
                valor={
                  datos.ultima_sincronizacion
                    ? momentoDeSincronizacion(datos.ultima_sincronizacion)
                    : "Sin sincronizaciones todavía"
                }
              />
            </div>
            <div className="ml-auto flex flex-none items-center gap-2">
              <PapeleraDeTerreno informeId={informeId} />
            </div>
          </div>

          <div className="border-divisor flex flex-wrap items-center gap-3 border-t pt-3">
            <div
              role="group"
              aria-label="Filtrar por tipo"
              className="bg-muted flex gap-[3px] rounded-lg p-[3px]"
            >
              {FILTROS.map(({ clave, etiqueta }) => (
                <button
                  key={clave}
                  type="button"
                  aria-pressed={filtro === clave}
                  onClick={() => setFiltro(clave)}
                  className={cn(
                    "focus-visible:ring-ring/50 inline-flex h-[1.875rem] items-center gap-[0.4375rem] rounded-md px-3 text-xs outline-none focus-visible:ring-[3px]",
                    filtro === clave
                      ? "bg-card text-ink font-bold shadow-sm"
                      : "text-ink-muted font-semibold",
                  )}
                >
                  {etiqueta}
                  <span className="text-ink-hint font-mono text-[0.65625rem] font-bold">
                    {conteos[clave]}
                  </span>
                </button>
              ))}
            </div>
            <p className="text-ink-muted min-w-[13.75rem] flex-1 text-xs font-semibold text-pretty">
              {datos.puede_descartar
                ? "Lo que el encargado sincronizó desde la App de terreno. Puedes descartar lo que no corresponda."
                : "Lo que el encargado sincronizó desde la App de terreno."}
            </p>
          </div>

          <FiltroDeSincronizaciones
            sincronizaciones={sincronizaciones}
            elegida={sincronizacionEfectiva}
            onElegir={setSincronizacion}
            marcando={marcarRevisado.isPending}
            onMarcarRevisado={() => {
              const ultima = sincronizaciones[sincronizaciones.length - 1];
              if (ultima) marcarRevisado.mutate(ultima.id);
            }}
          />
        </section>

        {sincronizacionEfectiva !== null &&
          secciones.length === 0 &&
          (notasGenerales ? cantidadDeNotas(notasGenerales) === 0 : true) && (
          <p className="border-border text-ink-muted rounded-xl border-[1.5px] border-dashed p-4 text-center text-[0.78125rem] font-semibold">
            Nada de lo recibido en esta sincronización se muestra con estos filtros.
          </p>
        )}
        {/* Las notas del PROYECTO no son una sección: van primero y sin número,
            para no renumerar las que sí lo son. */}
        {(sincronizacionEfectiva === null ||
          cantidadDeNotas(notasGenerales ?? datos.notas_generales) > 0) && (
          <TarjetaDeNotasGenerales
            informeId={informeId}
            encargado={datos.encargado?.nombre ?? null}
            notas={notasGenerales ?? datos.notas_generales}
          />
        )}

        {secciones.map((seccion, indice) => (
          <TarjetaDeSeccion
            key={seccion.clave}
            seccion={seccion}
            numero={indice + 1}
            informeId={informeId}
            secciones={secciones}
            filtro={filtro}
            onIrASeccion={irA}
            onAbrirPestana={onAbrirPestana}
            onAbrirFoto={setFotoAbierta}
          />
        ))}
      </div>

      <aside className="flex flex-col gap-3 lg:sticky lg:top-5">
        <nav
          aria-label="Secciones de terreno"
          className="border-border bg-card overflow-hidden rounded-xl border-[1.5px]"
        >
          <div className="border-divisor border-b px-3.5 pt-3 pb-2.5">
            <div className="text-ink text-[0.78125rem] font-bold tracking-[-0.01em]">
              Secciones de terreno
            </div>
            <div className="text-ink-muted mt-[0.1875rem] text-[0.6875rem] font-semibold">
              {[
                pluralizar(conteos.fotos, "foto", "fotos"),
                pluralizar(conteos.audios, "audio", "audios"),
                pluralizar(conteos.textos, "texto", "textos"),
              ].join(" · ")}
            </div>
          </div>
          <div className="flex flex-col gap-px px-1.5 pt-[0.3125rem] pb-[0.4375rem]">
            {secciones.map((seccion, i) => (
              <EntradaDelIndice
                key={seccion.clave}
                seccion={seccion}
                numero={i + 1}
                activa={activa === seccion.clave}
                onIr={() => irA(seccion.clave)}
              />
            ))}
          </div>
        </nav>

        <p className="border-border bg-superficie-sutil text-ink-muted rounded-xl border-[1.5px] px-3.5 py-3 text-[0.71875rem] leading-[1.45] font-semibold text-pretty">
          La transcripción de las notas de voz es automática y puede tener errores. Ante la duda,
          escuche el audio.
        </p>
      </aside>

      <VisorDeFotos
        informeId={informeId}
        fotos={fotos}
        abierta={fotoAbierta}
        onCambiar={setFotoAbierta}
      />
    </div>
  );
}

function DatoDelEncabezado({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div>
      <div className="text-ink-muted text-[0.59375rem] font-extrabold tracking-[0.06em] uppercase">
        {rotulo}
      </div>
      <div className="text-ink mt-0.5 font-mono text-xs font-bold">{valor}</div>
    </div>
  );
}

/**
 * Qué sección está a la vista, para resaltarla en el índice (scroll-spy).
 *
 * `IntersectionObserver` y no un oyente de `scroll`: el contenedor que
 * desplaza es el `main` del layout, y el observador mide contra lo que de
 * verdad se ve sin preguntar cuál es. Sin soporte (tests) queda la primera.
 */
function useSeccionActiva(claves: string[]): string | null {
  const [activa, setActiva] = useState<string | null>(null);
  const llave = claves.join("|");

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined" || llave === "") return;

    const visibles = new Set<string>();
    const orden = llave.split("|");

    const observador = new IntersectionObserver(
      (entradas) => {
        for (const e of entradas) {
          const clave = e.target.getAttribute("data-seccion");
          if (!clave) continue;
          if (e.isIntersecting) visibles.add(clave);
          else visibles.delete(clave);
        }
        // La PRIMERA visible en el orden del documento: es la que se está leyendo.
        const primera = orden.find((c) => visibles.has(c));
        if (primera) setActiva(primera);
      },
      { rootMargin: "-10% 0px -70% 0px" },
    );

    for (const clave of orden) {
      const el = document.getElementById(idDeLaSeccion(clave));
      if (el) observador.observe(el);
    }

    return () => observador.disconnect();
  }, [llave]);

  return activa ?? claves[0] ?? null;
}

function EntradaDelIndice({
  seccion,
  numero,
  activa,
  onIr,
}: {
  seccion: SeccionDeTerreno;
  numero: number;
  activa: boolean;
  onIr: () => void;
}) {
  const conteo = conteoDeLaSeccion(seccion);
  const total = totalDeMaterial(conteo);
  const hay = tieneContenido(conteo);
  const sinClasificar = seccion.pestana === null && hay;

  return (
    <button
      type="button"
      onClick={onIr}
      aria-current={activa ? "location" : undefined}
      className={cn(
        "hover:bg-muted focus-visible:ring-ring/50 flex min-w-0 items-center gap-[0.5625rem] rounded-md px-2 py-[0.4375rem] text-left outline-none focus-visible:ring-[3px]",
        activa && "bg-muted",
        hay ? "text-ink" : "text-ink-muted",
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "size-2 flex-none rounded-full border-[1.5px]",
          sinClasificar
            ? "bg-warning-dot border-warning-dot"
            : hay
              ? "bg-ink border-ink border-solid"
              : "border-ink-hint border-dashed bg-transparent",
        )}
      />
      <span className="text-ink-hint flex-none font-mono text-[0.59375rem] font-bold">
        {String(numero).padStart(2, "0")}
      </span>
      <span className={cn("min-w-0 flex-1 truncate text-xs", activa ? "font-bold" : "font-semibold")}>
        {seccion.nombre}
      </span>
      <span
        className={cn(
          "flex-none font-mono text-[0.65625rem] font-bold",
          sinClasificar ? "text-warning-soft-foreground" : hay ? "text-ink-body" : "text-ink-hint",
        )}
      >
        {total > 0 ? total : hay ? "GPS" : "—"}
      </span>
    </button>
  );
}

interface TarjetaDeSeccionProps {
  seccion: SeccionDeTerreno;
  numero: number;
  informeId: number;
  /** Todas las secciones: de ahí salen los destinos del selector «Clasificar». */
  secciones: SeccionDeTerreno[];
  filtro: FiltroDeTerreno;
  /** Lleva a otra sección de la misma pestaña (el «Ver» de una captura clasificada). */
  onIrASeccion: (clave: string) => void;
  onAbrirPestana: (pestana: SeccionInforme) => void;
  onAbrirFoto: (uuid: string) => void;
}

function TarjetaDeSeccion({
  seccion,
  numero,
  informeId,
  secciones,
  filtro,
  onIrASeccion,
  onAbrirPestana,
  onAbrirFoto,
}: TarjetaDeSeccionProps) {
  const conteo = conteoDeLaSeccion(seccion);
  const esSinClasificar = seccion.pestana === null;
  // Una carpeta sin registros TAMBIÉN es algo que el encargado creó: la sección
  // existe aunque todavía no traiga material.
  const conCarpetas = seccion.clave === "otros" && seccion.items.length > 0;
  const hay = tieneContenido(conteo) || conCarpetas;
  const pestanaAbrible = seccion.pestana
    ? PESTANAS.find((p) => p.seccion === seccion.pestana)
    : undefined;
  const etiquetaDeLaPestana = pestanaAbrible?.etiqueta;

  // Las que la web ya clasificó NO cuentan en `capturas` ni en `hay`, pero la
  // sección las lista: es desde donde se cambian o se devuelven.
  const clasificadasEnLaWeb = esSinClasificar ? (seccion.clasificadas_en_la_web ?? []) : [];

  const items = seccion.items.filter((i) => conCarpetas || itemVisible(i, filtro));
  const capturas = seccion.capturas.filter((r) => registroVisible(r, filtro));

  return (
    <section
      id={idDeLaSeccion(seccion.clave)}
      data-seccion={seccion.clave}
      className="border-border bg-card scroll-mt-5 rounded-xl border-[1.5px] px-5 pt-[1.125rem] pb-5"
    >
      <div className="flex items-center gap-3">
        <span
          className={cn(
            "flex size-[1.6875rem] flex-none items-center justify-center rounded-md font-mono text-[0.71875rem] font-bold",
            hay ? "bg-ink text-card" : "bg-muted text-ink-hint",
          )}
        >
          {String(numero).padStart(2, "0")}
        </span>
        <div className="min-w-0">
          <h3 className="text-ink text-[0.9375rem] font-bold tracking-[-0.01em]">{seccion.nombre}</h3>
          <p className="text-ink-muted mt-px text-xs font-semibold">{conCarpetas
              ? [
                  pluralizar(seccion.items.length, "carpeta", "carpetas"),
                  tieneContenido(conteo) ? descripcionDelConteo(conteo) : null,
                ]
                  .filter(Boolean)
                  .join(" · ")
              : descripcionDelConteo(conteo)}
          </p>
        </div>
        {seccion.pestana && etiquetaDeLaPestana && hay && (
          <button
            type="button"
            onClick={() => onAbrirPestana(pestanaAbrible!.seccion)}
            className="text-success-soft-foreground hover:text-ink focus-visible:ring-ring/50 ml-auto inline-flex flex-none items-center gap-[0.3125rem] rounded-sm text-xs font-bold outline-none focus-visible:ring-[3px]"
          >
            Abrir pestaña {etiquetaDeLaPestana}
            <ChevronRight className="size-[0.8125rem]" aria-hidden="true" />
          </button>
        )}
      </div>

      {esSinClasificar && hay && (
        <p className="bg-warning-soft text-warning-soft-foreground border-warning-dot/40 mt-3 rounded-lg border px-3 py-[0.5625rem] text-xs font-semibold text-pretty">
          El encargado no asignó estas capturas a ninguna sección. Clasifíquelas para que lleguen a
          la pestaña que corresponde: pueden contener datos para cualquiera.
        </p>
      )}

      {!hay && (
        <div className="border-border text-ink-muted mt-3.5 rounded-lg border-[1.5px] border-dashed p-4 text-center text-[0.78125rem] font-semibold">
          {clasificadasEnLaWeb.length > 0
            ? "No quedan capturas sin clasificar."
            : "El encargado no envió registros de esta sección."}
        </div>
      )}

      {hay &&
        seccion.campos
          .filter((c) => campoVisible(c, filtro))
          .map((campo) => (
            <BloqueDeCampo
              key={campo.clave}
              campo={campo}
              seccionNombre={seccion.nombre}
              informeId={informeId}
              filtro={filtro}
              onAbrirFoto={onAbrirFoto}
            />
          ))}

      {hay && items.length > 0 && (
        <>
          <div className="mt-[1.125rem] flex items-baseline gap-[0.5625rem]">
            <span className="text-ink text-[0.8125rem] font-bold">
              {seccion.items_titulo ?? "Registrados"}
            </span>
            <span className="text-ink-muted font-mono text-[0.6875rem] font-bold">{items.length}</span>
          </div>
          <div className="mt-[0.5625rem] flex flex-col gap-2.5">
            {items.map((item) => (
              <TarjetaDeItem
                key={item.referencia}
                item={item}
                seccionNombre={seccion.nombre}
                informeId={informeId}
                filtro={filtro}
                onAbrirFoto={onAbrirFoto}
              />
            ))}
          </div>
        </>
      )}

      {hay && capturas.length > 0 && (
        <div className="border-border mt-3.5 overflow-hidden rounded-xl border-[1.5px]">
          {capturas.map((registro) => (
            <FilaDeRegistro
              key={registro.uuid}
              registro={registro}
              numero={numeroDeRegistro(seccion.capturas.indexOf(registro))}
              contexto={`${seccion.nombre} · ${numeroDeRegistro(seccion.capturas.indexOf(registro))}`}
              informeId={informeId}
              filtro={filtro}
              onAbrirFoto={onAbrirFoto}
              pie={
                esSinClasificar ? (
                  <ClasificadorDeCaptura
                    informeId={informeId}
                    uuid={registro.uuid}
                    secciones={secciones}
                  />
                ) : undefined
              }
            />
          ))}
        </div>
      )}

      {clasificadasEnLaWeb.length > 0 && (
        <div className="border-border mt-3.5 overflow-hidden rounded-xl border-[1.5px]">
          <div className="bg-superficie-sutil text-ink-muted px-3.5 py-2 text-[0.6875rem] font-bold">
            Clasificadas desde la web · {clasificadasEnLaWeb.length}
          </div>
          {clasificadasEnLaWeb.map((registro) => (
            <FilaClasificadaEnLaWeb
              key={registro.uuid}
              informeId={informeId}
              registro={registro}
              secciones={secciones}
              onVer={onIrASeccion}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function BloqueDeCampo({
  campo,
  seccionNombre,
  informeId,
  filtro,
  onAbrirFoto,
}: {
  campo: CampoDeTerreno;
  seccionNombre: string;
  informeId: number;
  filtro: FiltroDeTerreno;
  onAbrirFoto: (uuid: string) => void;
}) {
  const ve = visibleConElFiltro(filtro);
  const meta = descripcionDelConteo({
    fotos: campo.fotos.length,
    audios: campo.audios.length,
    textos: campo.textos.length,
    gps: 0,
  });
  const vacio =
    campo.textos.length + campo.audios.length + campo.fotos.length === 0 &&
    !campo.gps &&
    !campo.portada;

  return (
    <div className="border-divisor mt-4 flex flex-col gap-[0.5625rem] border-t pt-3.5">
      <div className="flex items-baseline gap-2.5">
        <span className="text-ink text-[0.8125rem] font-bold">{campo.nombre}</span>
        <span className="text-ink-muted text-[0.6875rem] font-semibold">
          {campo.gps
            ? "Capturada en terreno"
            : campo.portada
              ? "Se usa en la portada del informe"
              : vacio
                ? ""
                : meta}
        </span>
      </div>

      {ve.textos &&
        campo.textos.map((t) => (
          <TarjetaDeTexto
            key={t.uuid}
            uuid={t.uuid}
            etiqueta={campo.nombre}
            texto={t.texto}
            capturado={t.capturado_en}
            sincronizado={t.sincronizado_en}
          />
        ))}

      {ve.audios &&
        campo.audios.map((a) => (
          <TarjetaDeAudio key={a.uuid} audio={a} informeId={informeId} etiqueta={campo.nombre} />
        ))}

      {ve.fotos && campo.fotos.length > 0 && (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(9.375rem,1fr))] gap-2.5">
          {campo.fotos.map((f) => (
            <TarjetaDeFoto
              key={f.uuid}
              informeId={informeId}
              foto={f}
              contexto={`${seccionNombre} · ${campo.nombre}`}
              onAbrir={() => onAbrirFoto(f.uuid)}
            />
          ))}
        </div>
      )}

      {ve.gps && campo.gps && <FilaDeGps gps={campo.gps} />}

      {ve.fotos && campo.portada && (
        <TarjetaDeFoto
          informeId={informeId}
          foto={campo.portada}
          contexto={`${seccionNombre} · Portada`}
          esPortada
          onAbrir={() => onAbrirFoto(campo.portada!.uuid)}
        />
      )}

      {vacio && filtro === "todo" && (
        <p className="text-ink-hint text-xs font-semibold">Sin registros del encargado.</p>
      )}
    </div>
  );
}

function FilaDeGps({ gps }: { gps: NonNullable<CampoDeTerreno["gps"]> }) {
  const lat = gps.latitud;
  const lng = gps.longitud;
  const tieneCoordenada = lat !== null && lng !== null;

  return (
    <div className="border-border bg-superficie-sutil flex flex-wrap items-center gap-3.5 rounded-lg border-[1.5px] px-[0.8125rem] py-[0.6875rem]">
      <MapPin className="text-success-soft-foreground size-[1.125rem] flex-none" aria-hidden="true" />
      <span className="text-ink font-mono text-[0.8125rem] font-bold">
        {tieneCoordenada ? `${lat}, ${lng}` : "Sin coordenadas"}
      </span>
      {gps.precision_m !== null && (
        <span className="text-ink-muted text-xs font-semibold">
          Precisión ± {gps.precision_m} m · capturada {horaDeCaptura(gps.capturado_en)}
        </span>
      )}
      {tieneCoordenada && (
        <a
          href={urlDelMapaDeTerreno(lat, lng)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-success-soft-foreground hover:text-ink text-xs font-bold"
        >
          Ver en el mapa
        </a>
      )}
      <span className="text-ink-muted ml-auto flex-none font-mono text-[0.65625rem] font-semibold">
        Sincronizado {momentoDeSincronizacion(gps.sincronizado_en)}
      </span>
    </div>
  );
}

/** El rótulo en versalitas con que se abre cada tarjeta de texto/audio. */
function Rotulo({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-ink-muted text-[0.59375rem] font-extrabold tracking-[0.07em] uppercase">
      {children}
    </span>
  );
}

function TarjetaDeTexto({
  uuid,
  etiqueta,
  texto,
  capturado,
  sincronizado,
}: {
  /** El bloque que se descarta; sin él (el texto de un registro) no hay botón propio. */
  uuid?: string;
  etiqueta?: string;
  texto: string;
  capturado: string | null;
  sincronizado: string | null;
}) {
  return (
    <div className="border-border bg-superficie-sutil rounded-lg border-[1.5px] px-3 pt-2.5 pb-[0.6875rem]">
      <div className="flex items-center gap-2">
        <Rotulo>Texto</Rotulo>
        <span className="text-ink-hint font-mono text-[0.65625rem] font-semibold">
          capturado {horaDeCaptura(capturado)}
        </span>
        <span className="text-ink-muted ml-auto flex-none font-mono text-[0.65625rem] font-semibold">
          Sincronizado {momentoDeSincronizacion(sincronizado)}
        </span>
        {uuid && <BotonDescartarDeTerreno uuid={uuid} etiqueta={`texto · ${etiqueta}`} />}
      </div>
      <p className="text-ink-body mt-[0.3125rem] text-[0.78125rem] leading-[1.55] font-medium text-pretty">
        {texto}
      </p>
    </div>
  );
}

function TarjetaDeAudio({
  audio,
  informeId,
  etiqueta,
}: {
  audio: AudioDeTerreno;
  informeId: number;
  etiqueta: string;
}) {
  return (
    <div className="border-border bg-superficie-sutil rounded-lg border-[1.5px] px-3 pt-2.5 pb-[0.6875rem]">
      <ReproductorDeAudioDeTerreno informeId={informeId} audio={audio} />
      <div className="mt-[0.5625rem] flex items-center gap-2">
        <Rotulo>Transcripción</Rotulo>
        <span className="text-ink-hint font-mono text-[0.65625rem] font-semibold">
          Nota de voz · capturada {horaDeCaptura(audio.capturado_en)}
        </span>
        <span className="text-ink-muted ml-auto flex-none font-mono text-[0.65625rem] font-semibold">
          Sincronizado {momentoDeSincronizacion(audio.sincronizado_en)}
        </span>
        <BotonDescartarDeTerreno uuid={audio.uuid} etiqueta={`nota de voz · ${etiqueta}`} />
      </div>
      {audio.transcripcion_estado === "lista" && audio.transcripcion ? (
        <p className="text-ink-body mt-[0.3125rem] text-[0.78125rem] leading-[1.55] font-medium text-pretty">
          {audio.transcripcion}
        </p>
      ) : (
        <p className="text-ink-hint mt-[0.3125rem] text-[0.78125rem] leading-[1.55] font-medium italic">
          {avisoDeTranscripcion(audio)}
        </p>
      )}
    </div>
  );
}

/**
 * Lo que el encargado anotó sobre cada dato de la construcción. Va bajo la
 * cabecera y no pegado a su dato: la cabecera es una fila corrida y una frase
 * larga la deformaría. Cada nota dice de qué dato habla.
 */
function NotasDeDatosDeConstruccion({ item }: { item: ItemDeTerreno }) {
  const notas = CAMPOS_DE_CONSTRUCCION.flatMap((campo) => {
    const nota = item.notas?.[campo];

    return nota ? [{ campo, nota }] : [];
  });

  if (notas.length === 0) return null;

  return (
    <ul
      aria-label={`Notas de terreno de ${item.nombre}`}
      className="border-border flex flex-col gap-1 border-t px-3.5 py-2"
    >
      {notas.map(({ campo, nota }) => (
        <li key={campo} className="text-ink-muted flex items-start gap-1.5 text-xs font-medium">
          <StickyNote aria-hidden className="mt-0.5 size-3 flex-none" />
          <span>
            <span className="text-ink font-bold">{etiquetaDelCampoDeConstruccion(campo)}</span>
            {": "}
            <span>{nota.texto}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

function TarjetaDeItem({
  item,
  seccionNombre,
  informeId,
  filtro,
  onAbrirFoto,
}: {
  item: ItemDeTerreno;
  seccionNombre: string;
  informeId: number;
  filtro: FiltroDeTerreno;
  onAbrirFoto: (uuid: string) => void;
}) {
  // Las carpetas de «Otros antecedentes» son los únicos ítems con `contenido`.
  const esCarpeta = item.contenido !== undefined;

  return (
    <div className="border-border overflow-hidden rounded-xl border-[1.5px]">
      <div className="bg-superficie-sutil flex flex-wrap items-center gap-2.5 px-3.5 py-2.5">
        <span className="text-ink text-[0.84375rem] font-bold">{item.nombre}</span>
        {item.nuevo && (
          <span className="bg-warning-soft text-warning-soft-foreground border-warning-dot/40 rounded-sm border px-1.5 py-0.5 text-[0.59375rem] font-extrabold tracking-[0.05em] uppercase">
            {esCarpeta ? "Creada en terreno" : "Nueva en terreno"}
          </span>
        )}
        {item.ausente === true && (
          <span className="bg-danger-soft text-danger-soft-foreground border-danger-dot/40 rounded-sm border px-1.5 py-0.5 text-[0.59375rem] font-extrabold tracking-[0.05em] uppercase">
            No está en el predio
          </span>
        )}
        {esCarpeta ? (
          <span className="text-ink-muted text-xs font-semibold">
            {item.contenido ? item.contenido : "Sin descripción"}
          </span>
        ) : (
          <div className="flex flex-wrap gap-3.5">
            {item.datos.map((d) => (
              <span key={d.etiqueta} className="text-ink-muted text-xs font-semibold">
                {d.etiqueta} <span className="text-ink font-bold">{d.valor}</span>
              </span>
            ))}
          </div>
        )}
        <span className="text-ink-muted ml-auto font-mono text-[0.65625rem] font-bold">
          {pluralizar(item.registros.length, "registro", "registros")}
        </span>
        {item.nuevo && (
          <BotonDescartarDeTerreno
            uuid={item.referencia.slice(item.referencia.lastIndexOf(":") + 1)}
            etiqueta={item.nombre}
          />
        )}
      </div>
      <NotasDeDatosDeConstruccion item={item} />
      {item.registros.map((registro, i) =>
        registroVisible(registro, filtro) ? (
          <FilaDeRegistro
            key={registro.uuid}
            registro={registro}
            numero={numeroDeRegistro(i)}
            contexto={`${seccionNombre} · ${item.nombre} · ${numeroDeRegistro(i)}`}
            informeId={informeId}
            filtro={filtro}
            onAbrirFoto={onAbrirFoto}
          />
        ) : null,
      )}
    </div>
  );
}

function FilaDeRegistro({
  registro,
  numero,
  contexto,
  informeId,
  filtro,
  onAbrirFoto,
  pie,
}: {
  registro: RegistroDeTerreno;
  numero: string;
  contexto: string;
  informeId: number;
  filtro: FiltroDeTerreno;
  onAbrirFoto: (uuid: string) => void;
  /** Lo que cierra la tarjeta (hoy, el selector «Clasificar» de las sin clasificar). */
  pie?: React.ReactNode;
}) {
  const ve = visibleConElFiltro(filtro);
  const fotos = ve.fotos ? fotosDelRegistro(registro).filter((f) => f.archivo !== null) : [];
  const conFoto = fotos.length > 0;
  const variasFotos = fotos.length > 1;

  return (
    <div
      data-captura={registro.uuid}
      className={cn(
        "border-divisor grid gap-3 border-t px-3.5 py-3",
        !conFoto && "grid-cols-[minmax(0,1fr)]",
        conFoto && !variasFotos && "grid-cols-[10.625rem_minmax(0,1fr)]",
        variasFotos && "grid-cols-[minmax(0,21.5rem)_minmax(0,1fr)]",
      )}
    >
      {conFoto && (
        <div className={cn(variasFotos && "grid grid-cols-2 content-start gap-2")}>
          {fotos.map((foto, i) => (
            <TarjetaDeFoto
              key={foto.uuid}
              informeId={informeId}
              foto={foto}
              contexto={variasFotos ? `${contexto} · Foto ${i + 1} de ${fotos.length}` : contexto}
              onAbrir={() => onAbrirFoto(foto.uuid)}
              sinDescartar={foto.uuid === registro.uuid}
            />
          ))}
        </div>
      )}
      <div className="flex min-w-0 flex-col gap-2">
        <div className="flex items-center gap-2">
          <span className="text-ink text-xs font-extrabold whitespace-nowrap">{numero}</span>
          <span className="text-ink-hint font-mono text-[0.65625rem] font-semibold whitespace-nowrap">
            capturado {horaDeCaptura(registro.capturado_en)}
          </span>
          <span className="text-ink-muted text-[0.6875rem] font-semibold">
            {tiposDelRegistro(registro)}
          </span>
          <span className="text-ink-muted ml-auto flex-none font-mono text-[0.65625rem] font-semibold">
            Sincronizado {momentoDeSincronizacion(registro.sincronizado_en)}
          </span>
          <BotonDescartarDeTerreno uuid={registro.uuid} etiqueta={`registro · ${contexto}`} />
        </div>
        {ve.audios &&
          registro.audios.map((a) => (
            <TarjetaDeAudio key={a.uuid} audio={a} informeId={informeId} etiqueta={contexto} />
          ))}
        {ve.textos && registro.texto && (
          <TarjetaDeTexto
            texto={registro.texto}
            capturado={registro.capturado_en}
            sincronizado={registro.sincronizado_en}
          />
        )}
        {pie}
      </div>
    </div>
  );
}
